import React, { Suspense, lazy, useEffect, useId, useRef, useState } from 'react'
import classNames from 'classnames'
import {
	IconBrandTelegram,
	IconBrandVk,
	IconBrandWhatsapp,
	IconChevronDown,
	IconLifebuoy,
	IconMail,
	IconMessageCircle,
	IconPhone,
} from '@tabler/icons-react'
import { useClickOutside } from '@utils/lib/useClickOutside'
import { usePageTitlePing } from '@utils/lib/usePageTitlePing'
import { SUPPORT_CHANNELS } from '@shared/ui/support-chat/channels'
import { on as onSupportEvent } from '@shared/ui/support-chat/supportApi'
import s from './support-button.module.scss'

// Ленивый импорт: assistant-ui попадает в бандл только при первом открытии чата
const SupportChatWindow = lazy(() =>
	import('@shared/ui/support-chat').then((m) => ({ default: m.SupportChatWindow }))
)

const CHAT_CHANNEL = {
	id: 'support-chat',
	type: 'chat',
	label: 'Чат на сайте',
	description: 'Онлайн-диалог с поддержкой',
}

const channelIcons = {
	telegram: IconBrandTelegram,
	whatsapp: IconBrandWhatsapp,
	vk: IconBrandVk,
	email: IconMail,
	phone: IconPhone,
	chat: IconMessageCircle,
}

export const DEFAULT_SUPPORT_CHANNELS = SUPPORT_CHANNELS.map(({ Icon, ...channel }) => channel)

const noop = () => {}

const getChannelIcon = (type) => {
	return channelIcons[type] || IconMessageCircle
}

export const SupportButton = ({
	channels = DEFAULT_SUPPORT_CHANNELS,
	label = 'Поддержка',
	ariaLabel = 'Открыть меню поддержки',
	icon,
	theme = 'primary',
	size = 'normal',
	shape = 'rounded',
	placement = 'bottom-end',
	className,
	buttonClassName,
	menuClassName,
	onOpenChange,
	disabled = false,
	withChat = true,
	// Режим «только чат»: без выпадающего меню каналов и без стрелочки —
	// клик сразу открывает чат поддержки (компактные контексты, например планшетный сайдбар)
	chatOnly = false,
	...props
}) => {
	const [isOpen, setOpen] = useState(false)
	const [isChatOpen, setChatOpen] = useState(false)
	// После первого открытия окно не размонтируем — рантайм и состояние чата сохраняются
	const [everOpenedChat, setEverOpenedChat] = useState(false)
	const [unreadCount, setUnreadCount] = useState(0)
	const wrapperRef = useRef(null)
	const menuId = useId()
	const Icon = icon || IconLifebuoy
	// chatOnly без чата не имеет смысла — откатываемся к обычному меню
	const directChat = chatOnly && withChat

	const isChatOpenRef = useRef(false)
	isChatOpenRef.current = isChatOpen

	usePageTitlePing(unreadCount > 0)

	// Непрочитанные: сообщения оператора, пока чат закрыт или вкладка в фоне
	useEffect(() => {
		if (typeof window === 'undefined') return undefined
		return onSupportEvent('message', (payload) => {
			if (payload?.channel !== 'outbound') return
			if (isChatOpenRef.current && !document.hidden) return
			setUnreadCount((count) => count + 1)
		})
	}, [])

	// Открытие чата сбрасывает счётчик
	useEffect(() => {
		if (isChatOpen) setUnreadCount(0)
	}, [isChatOpen])

	// Возврат на вкладку с открытым чатом тоже сбрасывает счётчик
	useEffect(() => {
		if (typeof document === 'undefined') return undefined
		const handleVisibility = () => {
			if (!document.hidden && isChatOpenRef.current) setUnreadCount(0)
		}
		document.addEventListener('visibilitychange', handleVisibility)
		return () => document.removeEventListener('visibilitychange', handleVisibility)
	}, [])

	const openChat = () => {
		setOpen(false)
		onOpenChange?.(false)
		setEverOpenedChat(true)
		setChatOpen(true)
	}

	const closeChat = () => {
		setChatOpen(false)
	}

	const closeMenu = () => {
		setOpen(false)
		onOpenChange?.(false)
	}

	useClickOutside(wrapperRef, closeMenu)

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				closeMenu()
			}
		}

		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
		}
	})

	const toggleMenu = () => {
		if (disabled) {
			return
		}

		if (directChat) {
			if (isChatOpen) {
				closeChat()
			} else {
				openChat()
			}
			return
		}

		setOpen((prev) => {
			onOpenChange?.(!prev)
			return !prev
		})
	}

	const handleChannelClick = (event, channel) => {
		if (channel.disabled) {
			event.preventDefault()
			return
		}

		channel.onClick?.(event, channel)
		closeMenu()
	}

	return (
		<div className={classNames(s.wrapper, className)} ref={wrapperRef}>
			<button
				type="button"
				className={classNames(
					s.button,
					s[theme],
					s[size],
					s[shape],
					isOpen && s.open,
					buttonClassName
				)}
				onClick={toggleMenu}
				disabled={disabled}
				{...props}
				aria-label={ariaLabel}
				aria-expanded={directChat ? isChatOpen : isOpen}
				aria-controls={directChat ? undefined : menuId}
				aria-haspopup={directChat ? 'dialog' : 'menu'}
			>
				<Icon size={20} stroke={1.8} className={s.buttonIcon} />
				{label ? <span className={s.buttonLabel}>{label}</span> : null}
				{!directChat && (
					<IconChevronDown size={18} stroke={1.8} className={s.chevron} />
				)}
			</button>

			{!directChat && (
			<nav
				id={menuId}
				className={classNames(
					s.menu,
					s[placement],
					isOpen && s.menuOpen,
					menuClassName
				)}
				aria-hidden={!isOpen}
			>
				{channels.length > 0 ? (
					<ul className={s.list} role="menu">
						{channels.map((channel) => {
							const ChannelIcon = channel.icon || getChannelIcon(channel.type)
							const itemContent = (
								<>
									<span className={classNames(s.channelIcon, channel.type && s[channel.type])}>
										<ChannelIcon size={20} stroke={1.8} />
									</span>
									<span className={s.channelText}>
										<span className={s.channelLabel}>{channel.label}</span>
										{channel.description ? (
											<span className={s.channelDescription}>{channel.description}</span>
										) : null}
									</span>
								</>
							)

							return (
								<li key={channel.id || channel.href || channel.label} role="none">
									{channel.href ? (
										<a
											className={classNames(s.channel, channel.disabled && s.disabled)}
											href={channel.href}
											target={channel.target}
											rel={channel.rel || (channel.target === '_blank' ? 'noreferrer' : undefined)}
											onClick={(event) => handleChannelClick(event, channel)}
											role="menuitem"
											aria-disabled={channel.disabled}
										>
											{itemContent}
										</a>
									) : (
										<button
											type="button"
											className={classNames(s.channel, channel.disabled && s.disabled)}
											onClick={(event) => handleChannelClick(event, channel)}
											disabled={channel.disabled}
											role="menuitem"
										>
											{itemContent}
										</button>
									)}
								</li>
							)
						})}
					</ul>
				) : (
					<div className={s.empty}>Каналы поддержки не настроены</div>
				)}

				{withChat && (
					<ul className={s.list} role="menu">
						<li role="none">
							<button
								type="button"
								className={s.channel}
								onClick={openChat}
								role="menuitem"
							>
								<span className={classNames(s.channelIcon, s.chat)}>
									<IconMessageCircle size={20} stroke={1.8} />
								</span>
								<span className={s.channelText}>
									<span className={s.channelLabel}>{CHAT_CHANNEL.label}</span>
									<span className={s.channelDescription}>{CHAT_CHANNEL.description}</span>
								</span>
							</button>
						</li>
				</ul>
				)}
			</nav>
			)}

			{unreadCount > 0 && (
				<span className={s.unreadBadge} aria-hidden>
					{unreadCount > 9 ? '9+' : unreadCount}
				</span>
			)}

			{everOpenedChat && isChatOpen && (
				<Suspense fallback={null}>
					<SupportChatWindow onClose={closeChat} />
				</Suspense>
			)}
		</div>
	)
}

SupportButton.defaultProps = {
	onOpenChange: noop,
}
